import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useLang } from '../../../i18n';
import { useToast } from '../../../components/Toast';
import { get, post, del } from '../../../services/api';
import Card from '../../../components/Card';
import Button from '../../../components/Button';
import Skeleton from '../../../components/Skeleton';

interface ApiToken {
  public_id: string;
  name: string;
  created_at: string;
}

interface Webhook {
  public_id: string;
  url: string;
  is_active: boolean;
}

export default function ProfileApi() {
  const { t } = useLang();
  const toast = useToast();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [tk, wh] = await Promise.all([
        get<{ results: ApiToken[] }>('/me/api-tokens/').catch(() => ({ results: [] })),
        get<{ results: Webhook[] }>('/me/webhooks/').catch(() => ({ results: [] })),
      ]);
      setTokens(tk.results || []);
      setWebhooks(wh.results || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createToken = async () => {
    try {
      await post('/me/api-tokens/', { name: `Token ${Date.now()}` });
      toast.success(t('profile.saved'));
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const revokeToken = async (id: string) => {
    try {
      await del(`/me/api-tokens/${id}/`);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  if (loading) return <Skeleton height={200} />;

  return (
    <div className="profile-api">
      <Card padding="md">
        <div className="profile-api__head">
          <h3 className="profile-api__title">{t('profile.api.tokens')}</h3>
          <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} onClick={createToken}>
            {t('profile.api.token_create')}
          </Button>
        </div>
        {tokens.length === 0 ? (
          <p style={{ color: 'var(--c-gray-500)' }}>—</p>
        ) : (
          <ul className="profile-api__list">
            {tokens.map((tk) => (
              <li key={tk.public_id} className="profile-api__item">
                <div>
                  <div style={{ fontWeight: 600 }}>{tk.name}</div>
                  <div style={{ fontSize: 'var(--fs-12)', color: 'var(--c-gray-500)' }}>{new Date(tk.created_at).toLocaleString()}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => revokeToken(tk.public_id)}>{t('profile.api.token_revoke')}</Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card padding="md">
        <h3 className="profile-api__title">{t('profile.api.webhooks')}</h3>
        {webhooks.length === 0 ? (
          <p style={{ color: 'var(--c-gray-500)' }}>—</p>
        ) : (
          <ul className="profile-api__list">
            {webhooks.map((w) => (
              <li key={w.public_id} className="profile-api__item">
                <span style={{ wordBreak: 'break-all' }}>{w.url}</span>
                <span>{w.is_active ? '●' : '○'}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
